import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Images } from "../../../utils/imageConstants";
import type { ITeams, ILeagueTeamSnapshotResponse, ITeamSnapshotOption } from "./types/teams";
import type {
  IDivisionResponse,
  ICreateDivisionRequest,
  IDivisionPutNewTeamItem,
  IDivisionTeamEntry,
} from "../../myTeam/types/myTeam";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import { createDivision, fetchDivisions, updateDivision } from "../../myTeam/api/myTeamApi";
import toast from "react-hot-toast";
import AddTeamsModal from "./AddTeamsModal";
import FormDropdown from "../../../components/common/FormDropdown";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeague, selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";
import type { IGetAllTournaments, ITournamentsByLeagueResponse } from "../types/home";
import { extractDivisionListFromPayload, extractTeamsFromGetAllResponse, getTeamsFromDivisionNode, normalizeDivisionList } from "./divisionTeamUtils";
import { normalizeImageFileTypeForApi } from "../../../helper/imageFileTypeForApi";
import { canManageDivisionsInLeague } from "../../../utils/leagueAccess";

/** GET `league/teamSnapshot/{leagueId}` — path kept literal so Network clearly shows `teamSnapshot`. */
async function fetchLeagueTeamSnapshot(
  leagueId: string,
  signal?: AbortSignal
): Promise<ILeagueTeamSnapshotResponse> {
  const path = `league/teamSnapshot/${leagueId}`;
  return (await Api.get<ILeagueTeamSnapshotResponse>(path, signal ? { signal } : undefined)).data;
}

function readSessionLeagueId(): string | null {
  try {
    const v = sessionStorage.getItem("leagueId")?.trim();
    return v || null;
  } catch {
    return null;
  }
}

/** First non-empty id — avoids `??` keeping `""` and skipping later fallbacks. */
function pickLeagueId(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const c of candidates) {
    const t = (c ?? "").trim();
    if (t) return t;
  }
  return null;
}

/** Backend may return `tournamentResponses`, `tournamentResponseList`, or nest under `data`. */
function extractTournamentsFromLeagueApiPayload(
  payload: ITournamentsByLeagueResponse | null | undefined
): IGetAllTournaments[] {
  if (!payload) return [];
  const r = payload as unknown as Record<string, unknown>;
  const dataObj = r.data as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    payload.tournamentResponses,
    r.tournamentResponseList,
    dataObj?.tournamentResponses,
    dataObj?.tournamentResponseList,
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c as IGetAllTournaments[];
  }
  return Array.isArray(payload.tournamentResponses) ? payload.tournamentResponses : [];
}

interface TeamChip {
  teamId: string;
  teamName: string;
  teamSnapshotId?: string;
  /** Code used in PUT `newTeams` / POST leaf when `requiresNewTeamPut` is set */
  teamCode?: string;
  /** true = was already saved in the division (cannot remove); false/undefined = added this session (removable) */
  isOriginal?: boolean;
  /**
   * Mirrors Dart `isNewlyAdded` — true when this team is being newly added to the division.
   * Frontend-only flag; not serialized to JSON.
   */
  isNewlyAdded?: boolean;
  /**
   * Created via "Create new team" in this flow: backend expects assignment through
   * `newTeams.teamSnapshotPutRequestList` (not `teamSnapShotIdList`), even if create API returned a snapshot id.
   * Also set for teams selected from the listing page ("Add Teams") so they go through the same PUT path.
   */
  requiresNewTeamPut?: boolean;
  /**
   * true = `teamId` is a client-side placeholder UUID (session-created team, no real backend id yet).
   * When false/undefined, `teamId` is a real backend id and should be forwarded in PUT payload.
   */
  isLocalId?: boolean;
  /** Description entered in create-team modal — forwarded to PUT newTeams */
  description?: string;
  /** Base64-encoded image and MIME type from create-team modal — forwarded to PUT newTeams */
  imageBase64?: string;
  imageFileType?: string;
  /** Local file path for image preview — frontend-only, not serialized */
  imagePath?: string;
  // Fields from TeamElementV2 / TeamSnapshotOptions — forwarded in PUT newTeams payload
  captainPlayerId?: string | null;
  viceCaptainPlayerId?: string | null;
  wkPlayerId?: string | null;
  leagueId?: string | null;
  groundId?: string | null;
}

interface GroupState {
  divisionId?: string;
  name: string;
  teams: TeamChip[];
  showPicker: boolean;
  originalTeamSnapshotIds?: string[];
}

interface DivisionState {
  divisionId?: string;
  name: string;
  groups: GroupState[];
}

interface SessionTeam {
  teamId: string;
  teamName: string;
  teamSnapshotId?: string;
  teamLogoUrl?: string | null;
  winMatchesCount?: number;
  lossMatchesCount?: number;
  drawMatchesCount?: number;
}

const addTeamsSelectionKey = (t: SessionTeam) => (t.teamSnapshotId ?? "").trim() || t.teamId;

function teamChipThumbDataUrl(chip: TeamChip): string | null {
  if (chip.imageBase64 && chip.imageFileType) {
    return `data:${chip.imageFileType};base64,${chip.imageBase64}`;
  }
  return null;
}

/** True if the same tournament team (by id or snapshot id) already sits in another group/division. */
function isSessionTeamAssignedOutsideTargetGroup(
  layout: DivisionState[],
  targetDivIdx: number,
  targetGrpIdx: number,
  t: SessionTeam
): boolean {
  const tid = (t.teamId ?? "").trim();
  const snap = (t.teamSnapshotId ?? "").trim();
  for (let di = 0; di < layout.length; di++) {
    for (let gi = 0; gi < layout[di].groups.length; gi++) {
      if (di === targetDivIdx && gi === targetGrpIdx) continue;
      for (const chip of layout[di].groups[gi].teams) {
        const cid = (chip.teamId ?? "").trim();
        const cs = (chip.teamSnapshotId ?? "").trim();
        if (tid && (tid === cid || tid === cs)) return true;
        if (snap && (snap === cid || snap === cs)) return true;
      }
    }
  }
  return false;
}

function teamSnapshotOptionToITeams(
  opt: ITeamSnapshotOption,
  tournamentId: string,
  leagueId: string | null
): ITeams {
  return {
    teamSnapshotId: opt.teamSnapshotId,
    team: { teamId: opt.teamId, teamName: opt.teamName },
    teamCode: opt.teamCode,
    description: opt.description ?? null,
    groundId: opt.groundId,
    leagueId,
    tournamentId,
  };
}

/** Team short code — same rules as league stepper. */
const normalizeTeamCodeForDivision = (teamName: string, teamCode?: string) => {
  const trimmed = (teamCode ?? "").trim();
  if (trimmed) return trimmed;
  const fallback = teamName.replace(/[^A-Za-z0-9]/g, "").slice(0, 10).toUpperCase();
  return fallback || "TEAM";
};

const resolveChipRow = (chip: TeamChip, tournamentTeams: ITeams[]) =>
  tournamentTeams.find(
    (at) =>
      (!!chip.teamSnapshotId && at.teamSnapshotId === chip.teamSnapshotId) ||
      at.team.teamId === chip.teamId ||
      at.teamSnapshotId === chip.teamId
  );

/** Tournament snapshot id for PUT `teamSnapShotIdList` (prefer row from getAllTeam). */
const resolveSnapshotIdForPut = (chip: TeamChip, tournamentTeams: ITeams[]): string => {
  const row = resolveChipRow(chip, tournamentTeams);
  const fromRow = (row?.teamSnapshotId ?? "").trim();
  const fromChip = (chip.teamSnapshotId ?? "").trim();
  return fromRow || fromChip;
};

/** POST /division may return a string id, JSON string, or wrapped object. */
const parseCreateDivisionResponseId = (raw: unknown): string => {
  if (raw == null) return "";
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const id = o.divisionId ?? o.id;
    if (typeof id === "string") return id.trim();
    return "";
  }
  const s = String(raw).trim();
  if (!s) return "";
  try {
    const parsed = JSON.parse(s) as unknown;
    if (typeof parsed === "string") return parsed.trim();
    if (parsed && typeof parsed === "object") {
      const o = parsed as Record<string, unknown>;
      const id = o.divisionId ?? o.id;
      if (typeof id === "string") return id.trim();
    }
  } catch {
    /* body is raw uuid string */
  }
  return s.replace(/^"|"$/g, "").trim();
};

/**
 * Leaf division body matching the published curl (Group A example):
 * subDivisions: null, childDivisions: [], teamIds + optional minimal teamSnapshotPutRequestList.
 */
const buildGroupLeafDivisionPayload = (
  grp: GroupState,
  tournamentId: string,
  tournamentTeams: ITeams[]
): ICreateDivisionRequest => {
  const teamIds: string[] = [];
  const putListMinimal: Array<{ teamName: string; teamCode: string; tournamentId: string }> = [];

  for (const chip of grp.teams) {
    if (chip.requiresNewTeamPut) {
      putListMinimal.push({
        teamName: chip.teamName,
        teamCode: chip.teamCode ?? normalizeTeamCodeForDivision(chip.teamName, undefined),
        tournamentId,
      });
      continue;
    }
    const row = resolveChipRow(chip, tournamentTeams);
    const snap = (chip.teamSnapshotId ?? row?.teamSnapshotId ?? "").trim();
    const baseId = (row?.team?.teamId ?? chip.teamId).trim();

    if (snap) {
      teamIds.push(snap);
    } else if (baseId) {
      teamIds.push(baseId);
    } else {
      putListMinimal.push({
        teamName: chip.teamName,
        teamCode: normalizeTeamCodeForDivision(chip.teamName, undefined),
        tournamentId,
      });
    }
  }

  const uniqueTeamIds = Array.from(new Set(teamIds.filter(Boolean)));

  return {
    divisionName: grp.name,
    tournamentId,
    teamIds: uniqueTeamIds,
    childDivisions: [],
    teamSnapshotPutRequestList:
      putListMinimal.length > 0
        ? { teamSnapshotPutRequestList: putListMinimal }
        : null,
    subDivisions: null,
  };
};

/**
 * API links parent → children via `childDivisions: string[]` (existing child division ids).
 * Create each group as its own POST (curl shape), then parent with those ids — nested
 * `subDivisions` alone is often not returned on GET, so groups never appeared in the UI.
 */
const createDivisionTreeViaChildIds = async (
  divisionStates: DivisionState[],
  tournamentId: string,
  tournamentTeams: ITeams[]
): Promise<void> => {
  for (const div of divisionStates) {
    const childDivisionIds: string[] = [];
    for (const grp of div.groups) {
      const leafPayload = buildGroupLeafDivisionPayload(grp, tournamentId, tournamentTeams);
      const rawId = await createDivision(leafPayload);
      const id = parseCreateDivisionResponseId(rawId);
      if (!id) {
        toast.error(
          `Could not read division id after creating group "${grp.name}". The parent may be incomplete.`
        );
        throw new Error("createDivision: missing division id in response");
      }
      childDivisionIds.push(id);
    }

    const parentPayload: ICreateDivisionRequest = {
      divisionName: div.name,
      tournamentId,
      teamIds: [],
      childDivisions: childDivisionIds,
      teamSnapshotPutRequestList: null,
      subDivisions: null,
    };
    await createDivision(parentPayload);
  }
};

const buildInitialState = (numDivisions: number, numGroups: number): DivisionState[] =>
  Array.from({ length: numDivisions }, (_, di) => ({
    name: `Division ${di + 1}`,
    groups: Array.from({ length: numGroups }, (_, gi) => ({
      name: `Group ${gi + 1}`,
      teams: [],
      showPicker: false,
    })),
  }));

const buildFromExisting = (divisions: IDivisionResponse[]): DivisionState[] =>
  divisions.map((div) => ({
    divisionId: div.divisionId,
    name: div.divisionName,
    groups: (div.childDivisions ?? []).map((grp) => {
      const teams = (getTeamsFromDivisionNode(grp) as IDivisionTeamEntry[])
        .map((t) => {
          // API may return either `teamSnapshotId` (lowercase s) or `teamSnapShotId` (uppercase S)
          const rawT = t as unknown as Record<string, unknown>;
          const snapId = ((t.teamSnapshotId || rawT["teamSnapShotId"] as string | undefined) ?? "").trim();
          return {
            teamId: t.team?.teamId ?? t.teamId ?? snapId ?? "",
            teamName: t.team?.teamName ?? t.teamName ?? "",
            teamSnapshotId: snapId,
            teamCode: t.teamCode ?? undefined,
            isOriginal: true,
            isNewlyAdded: false,
            description: t.team?.description ?? t.description ?? undefined,
            captainPlayerId: t.captainPlayerId ?? undefined,
            viceCaptainPlayerId: t.viceCaptainPlayerId ?? undefined,
            wkPlayerId: t.wkPlayerId ?? undefined,
            leagueId: t.leagueId ?? undefined,
            groundId: t.team?.groundId ?? t.groundId ?? undefined,
          } as TeamChip;
        })
        .filter((t) => t.teamId);
      return {
        divisionId: grp.divisionId,
        name: grp.divisionName,
        showPicker: false,
        teams,
        originalTeamSnapshotIds: teams
          .map((t) => t.teamSnapshotId)
          .filter((id): id is string => !!id),
      };
    }),
  }));

const CreateDivisionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const ctFileRef = useRef<HTMLInputElement | null>(null);

  const state = (location.state ?? {}) as {
    mode?: "create" | "edit";
    numDivisions?: number;
    numGroups?: number;
    divisions?: IDivisionResponse[];
    leagueId?: string;
  };

  const mode = state.mode ?? "create";
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const canManageDivisions = canManageDivisionsInLeague(selectedLeague);
  const selectedLeagueIdFromSlice = useAppSelector(selectSelectedLeagueId);
  const leagueIdFromNav = typeof state.leagueId === "string" ? state.leagueId.trim() : "";
  // Redux league object, slice id, route state, session — pick first non-empty (empty string must not block fallbacks).
  const resolvedLeagueId = useMemo(
    () =>
      pickLeagueId(
        selectedLeague?.leagueId,
        selectedLeagueIdFromSlice,
        leagueIdFromNav,
        readSessionLeagueId()
      ),
    [selectedLeague?.leagueId, selectedLeagueIdFromSlice, leagueIdFromNav]
  );

  const [leagueTeamSnapshot, setLeagueTeamSnapshot] = useState<ILeagueTeamSnapshotResponse | null>(null);
  const [teamSnapshotLoading, setTeamSnapshotLoading] = useState(false);
  const [snapshotRefreshKey, setSnapshotRefreshKey] = useState(0);

  const [activeTournamentId, setActiveTournamentId] = useState(() => {
    try {
      return sessionStorage.getItem("tournamentId") ?? "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (!leagueIdFromNav) return;
    try {
      sessionStorage.setItem("leagueId", leagueIdFromNav);
    } catch {
      /* ignore */
    }
  }, [leagueIdFromNav]);

  const applyTournamentContext = useCallback((id: string, name?: string, leagueIdForSession?: string | null) => {
    setActiveTournamentId(id);
    try {
      sessionStorage.setItem("tournamentId", id);
      if (name != null && name !== "") sessionStorage.setItem("tournamentName", name);
      if (leagueIdForSession != null && leagueIdForSession !== "") {
        sessionStorage.setItem("leagueId", leagueIdForSession);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const { data: tournamentsByLeague, isLoading: tournamentsLoading } = useQuery<ITournamentsByLeagueResponse>({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, resolvedLeagueId],
    queryFn: async () =>
      (
        await Api.get<ITournamentsByLeagueResponse>(
          `${QUERY_KEY.getTournamentsByLeagues}/${resolvedLeagueId}`
        )
      ).data,
    enabled: !!resolvedLeagueId,
  });

  // Imperative fetch (not useQuery) so the XHR always fires when we have a league id; avoids RQ `enabled`/cache edge cases.
  useEffect(() => {
    if (!resolvedLeagueId) {
      setLeagueTeamSnapshot(null);
      setTeamSnapshotLoading(false);
      return;
    }
    const ac = new AbortController();
    setTeamSnapshotLoading(true);
    fetchLeagueTeamSnapshot(resolvedLeagueId, ac.signal)
      .then((data) => {
        setLeagueTeamSnapshot(data);
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        const code = (err as { code?: string })?.code;
        if (code === "ERR_CANCELED" || code === "ECONNABORTED") return;
        console.error("[CreateDivisionPage] league/teamSnapshot failed:", err);
        setLeagueTeamSnapshot(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setTeamSnapshotLoading(false);
      });
    return () => ac.abort();
  }, [resolvedLeagueId, snapshotRefreshKey]);

  // Same approach as Tournaments.tsx — access tournamentResponses directly
  const leagueTournaments = tournamentsByLeague?.tournamentResponses ?? [];

  useEffect(() => {
    const lid = tournamentsByLeague?.leagueResponse?.leagueId?.trim();
    if (lid) {
      try {
        sessionStorage.setItem("leagueId", lid);
      } catch {
        /* ignore */
      }
    }
  }, [tournamentsByLeague?.leagueResponse?.leagueId]);

  useEffect(() => {
    const lid = leagueTeamSnapshot?.leagueId?.trim();
    if (lid) {
      try {
        sessionStorage.setItem("leagueId", lid);
      } catch {
        /* ignore */
      }
    }
  }, [leagueTeamSnapshot?.leagueId]);

  /** Prefer tournaments from team-snapshot API when present; else league tournament list. */
  useEffect(() => {
    const snapOpts = leagueTeamSnapshot?.tournamentTeamSnapshotOptions;
    if (snapOpts && snapOpts.length > 0) {
      const inList = snapOpts.some((t) => t.tournamentId === activeTournamentId);
      if (!inList) {
        const next = snapOpts[0];
        applyTournamentContext(next.tournamentId, next.tournamentName, resolvedLeagueId ?? null);
      }
      return;
    }
    if (!leagueTournaments.length) return;
    const inList = leagueTournaments.some((t) => t.tournamentId === activeTournamentId);
    if (!inList) {
      const next = leagueTournaments[0];
      applyTournamentContext(next.tournamentId, next.tournamentName, next.leagueId ?? null);
    }
  }, [leagueTeamSnapshot, leagueTournaments, activeTournamentId, applyTournamentContext, resolvedLeagueId]);

  const tournamentDropdownOptions = useMemo(() => {
    if (leagueTournaments.length > 0) {
      return leagueTournaments.map((t) => ({ value: t.tournamentId, label: t.tournamentName }));
    }
    if (!activeTournamentId) return [];
    let label = "Tournament";
    try {
      label = sessionStorage.getItem("tournamentName")?.trim() || label;
    } catch {
      /* ignore */
    }
    return [{ value: activeTournamentId, label }];
  }, [leagueTournaments, activeTournamentId]);

  /** Add Teams screen: tournaments from GET /league/teamSnapshot/{leagueId}, else league list. */
  const addTeamsTournamentDropdownOptions = useMemo(() => {
    const snap = leagueTeamSnapshot?.tournamentTeamSnapshotOptions;
    if (snap && snap.length > 0) {
      return snap.map((t) => ({ value: t.tournamentId, label: t.tournamentName }));
    }
    return tournamentDropdownOptions;
  }, [leagueTeamSnapshot?.tournamentTeamSnapshotOptions, tournamentDropdownOptions]);

  // ─── Division state ───────────────────────────────────────────────────────
  const [divisions, setDivisions] = useState<DivisionState[]>(() => {
    if (mode === "edit" && state.divisions?.length) {
      return buildFromExisting(normalizeDivisionList(state.divisions));
    }
    return buildInitialState(state.numDivisions ?? 1, state.numGroups ?? 1);
  });
  const [editingDivision, setEditingDivision] = useState<number | null>(null);
  const [editingDivisionName, setEditingDivisionName] = useState("");
  const [saving, setSaving] = useState(false);

  // ─── Inner-view state ─────────────────────────────────────────────────────
  // "divisions" = main page, "addTeams" = listing page
  const [innerView, setInnerView] = useState<"divisions" | "addTeams">("divisions");
  const [pickerTarget, setPickerTarget] = useState<{ divIdx: number; grpIdx: number } | null>(null);
  // Preserved copy used by the listing page — never nulled by modal onClose
  const addTeamsTarget = React.useRef<{ divIdx: number; grpIdx: number } | null>(null);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);

  // Listing-page state
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  // Teams created in this session that appear in the listing before cache refresh
  const [sessionNewTeams, setSessionNewTeams] = useState<SessionTeam[]>([]);

  const onAddTeamsTournamentChange = useCallback(
    (id: string) => {
      const snapRow = leagueTeamSnapshot?.tournamentTeamSnapshotOptions?.find((t) => t.tournamentId === id);
      const leagueRow = leagueTournaments.find((t) => t.tournamentId === id);
      applyTournamentContext(
        id,
        snapRow?.tournamentName ?? leagueRow?.tournamentName ?? "",
        leagueRow?.leagueId ?? resolvedLeagueId ?? null
      );
      setSelectedTeamIds(new Set());
    },
    [leagueTeamSnapshot, leagueTournaments, applyTournamentContext, resolvedLeagueId]
  );

  // Create-team form state
  const [ctTeamName, setCtTeamName] = useState("");
  const [ctTeamCode, setCtTeamCode] = useState("");
  const [ctDescription, setCtDescription] = useState("");
  const [ctImageFile, setCtImageFile] = useState<File | null>(null);
  const [ctImageName, setCtImageName] = useState("");
  const [ctApproveNewPlayers, setCtApproveNewPlayers] = useState(false);

  // ─── Queries ──────────────────────────────────────────────────────────────
  // Fetch league teams (not tournament teams) so ALL league teams are available
  // to assign to divisions — including teams not yet in any tournament snapshot.
  const { data: allTeams = [] } = useQuery<ITeams[]>({
    queryKey: [QUERY_KEY.getAllTeam, resolvedLeagueId],
    queryFn: async () => {
      const response = await Api.get(`${QUERY_KEY.getAllTeam}/${resolvedLeagueId}`);
      return extractTeamsFromGetAllResponse(response?.data);
    },
    enabled: !!resolvedLeagueId,
  });

  const snapshotTournamentRows = useMemo(() => {
    const entry = leagueTeamSnapshot?.tournamentTeamSnapshotOptions?.find(
      (x) => x.tournamentId === activeTournamentId
    );
    if (!entry) return [] as ITeams[];
    return entry.teamSnapshotOptions.map((opt) =>
      teamSnapshotOptionToITeams(opt, activeTournamentId, resolvedLeagueId ?? null)
    );
  }, [leagueTeamSnapshot?.tournamentTeamSnapshotOptions, activeTournamentId, resolvedLeagueId]);

  /** Merge teamv2 league list with snapshot rows for the active tournament (PUT/create division resolution). */
  const teamsMergedForDivision = useMemo(() => {
    const bySnap = new Map<string, ITeams>();
    for (const t of allTeams) {
      const id = (t.teamSnapshotId ?? "").trim();
      if (id) bySnap.set(id, t);
    }
    for (const t of snapshotTournamentRows) {
      bySnap.set(t.teamSnapshotId, t);
    }
    return Array.from(bySnap.values());
  }, [allTeams, snapshotTournamentRows]);

  /** Team ids / snapshot ids already placed in any group under any division — cannot add the same tournament team twice. */
  const teamsAlreadyAssignedAcrossDivisions = useMemo(() => {
    const ids = new Set<string>();
    for (const div of divisions) {
      for (const grp of div.groups) {
        for (const chip of grp.teams) {
          const tid = (chip.teamId ?? "").trim();
          if (tid) ids.add(tid);
          const snap = (chip.teamSnapshotId ?? "").trim();
          if (snap) ids.add(snap);
        }
      }
    }
    return ids;
  }, [divisions]);

  // Add Teams listing: prefer GET /league/teamSnapshot/{leagueId} for the active tournament; else teamv2 list.
  const listingTeams: SessionTeam[] = useMemo(() => {
    const snapEntry = leagueTeamSnapshot?.tournamentTeamSnapshotOptions?.find(
      (x) => x.tournamentId === activeTournamentId
    );
    const fromSnapshot: SessionTeam[] =
      snapEntry?.teamSnapshotOptions.map((opt) => ({
        teamId: opt.teamId,
        teamName: opt.teamName,
        teamSnapshotId: opt.teamSnapshotId,
        teamLogoUrl: opt.teamLogoUrl,
        winMatchesCount: opt.winMatchesCount,
        lossMatchesCount: opt.lossMatchesCount,
        drawMatchesCount: opt.drawMatchesCount,
      })) ?? [];

    const fromApiFallback: SessionTeam[] = allTeams
      .filter((t) => {
        const id = t.team.teamId;
        const snap = t.teamSnapshotId;
        if (teamsAlreadyAssignedAcrossDivisions.has(id)) return false;
        if (snap && teamsAlreadyAssignedAcrossDivisions.has(snap)) return false;
        return true;
      })
      .map((t) => ({ teamId: t.team.teamId, teamName: t.team.teamName, teamSnapshotId: t.teamSnapshotId }));

    const base = fromSnapshot.length > 0 ? fromSnapshot : fromApiFallback;

    const filtered = base.filter((t) => {
      if (teamsAlreadyAssignedAcrossDivisions.has((t.teamId ?? "").trim())) return false;
      const sk = addTeamsSelectionKey(t);
      if (sk && teamsAlreadyAssignedAcrossDivisions.has(sk)) return false;
      return true;
    });

    const apiKeys = new Set(filtered.map(addTeamsSelectionKey));
    const freshNew = sessionNewTeams.filter((t) => {
      const key = addTeamsSelectionKey(t);
      if (apiKeys.has(key)) return false;
      if (teamsAlreadyAssignedAcrossDivisions.has((t.teamId ?? "").trim())) return false;
      if (t.teamSnapshotId && teamsAlreadyAssignedAcrossDivisions.has(t.teamSnapshotId.trim())) return false;
      return true;
    });
    return [...filtered, ...freshNew];
  }, [leagueTeamSnapshot, activeTournamentId, allTeams, teamsAlreadyAssignedAcrossDivisions, sessionNewTeams]);

  // ─── Division helpers ─────────────────────────────────────────────────────
  const updateGroup = (divIdx: number, grpIdx: number, patch: Partial<GroupState>) => {
    setDivisions((prev) =>
      prev.map((div, di) =>
        di !== divIdx ? div : { ...div, groups: div.groups.map((grp, gi) => gi !== grpIdx ? grp : { ...grp, ...patch }) }
      )
    );
  };

  const removeTeamFromGroup = (divIdx: number, grpIdx: number, teamId: string) => {
    setDivisions((prev) =>
      prev.map((div, di) =>
        di !== divIdx
          ? div
          : { ...div, groups: div.groups.map((grp, gi) => gi !== grpIdx ? grp : { ...grp, teams: grp.teams.filter((t) => t.teamId !== teamId) }) }
      )
    );
  };

  const deleteDiv = (divIdx: number) => {
    setDivisions((prev) => prev.filter((_, i) => i !== divIdx));
  };

  const startEditDivisionName = (divIdx: number) => {
    setEditingDivision(divIdx);
    setEditingDivisionName(divisions[divIdx].name);
  };

  const saveDivisionName = (divIdx: number) => {
    setDivisions((prev) =>
      prev.map((div, i) => i !== divIdx ? div : { ...div, name: editingDivisionName.trim() || div.name })
    );
    setEditingDivision(null);
  };

  // ─── Listing page handlers ────────────────────────────────────────────────
  const toggleTeamSelection = (id: string) => {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUpdateDivision = () => {
    const target = addTeamsTarget.current;
    if (!target) return;
    const { divIdx, grpIdx } = target;
    const selected = listingTeams.filter((t) => selectedTeamIds.has(addTeamsSelectionKey(t)));
    setDivisions((prev) =>
      prev.map((div, di) => {
        if (di !== divIdx) return div;
        return {
          ...div,
          groups: div.groups.map((grp, gi) => {
            if (gi !== grpIdx) return grp;
            const incoming = selected
              .filter((t) => !isSessionTeamAssignedOutsideTargetGroup(prev, divIdx, grpIdx, t))
              .filter(
                (t) =>
                  !grp.teams.some(
                    (existing) =>
                      existing.teamId === t.teamId ||
                      (!!t.teamSnapshotId && existing.teamSnapshotId === t.teamSnapshotId)
                  )
              )
              .map((t) => {
                const fullRow =
                  teamsMergedForDivision.find(
                    (at) =>
                      at.team.teamId === t.teamId ||
                      (!!t.teamSnapshotId && at.teamSnapshotId === t.teamSnapshotId)
                  ) ??
                  allTeams.find(
                    (at) =>
                      at.team.teamId === t.teamId ||
                      (!!t.teamSnapshotId && at.teamSnapshotId === t.teamSnapshotId)
                  );
                return {
                  teamId: t.teamId,
                  teamName: t.teamName,
                  teamSnapshotId: t.teamSnapshotId,
                  teamCode: fullRow?.teamCode ?? undefined,
                  isOriginal: false,
                  isNewlyAdded: true,
                  // Use the same PUT path as "Create new team" so the backend receives a
                  // teamSnapshotPutRequestList entry (with real teamId) instead of a bare snapshot id.
                  requiresNewTeamPut: true,
                  isLocalId: false,
                  description: fullRow?.description ?? undefined,
                  captainPlayerId: fullRow?.captainPlayerId ?? undefined,
                  viceCaptainPlayerId: fullRow?.viceCaptainPlayerId ?? undefined,
                  wkPlayerId: fullRow?.wkPlayerId ?? undefined,
                  leagueId: fullRow?.leagueId ?? resolvedLeagueId ?? undefined,
                  groundId: fullRow?.groundId ?? undefined,
                } as TeamChip;
              });
            return { ...grp, teams: [...grp.teams, ...incoming] };
          }),
        };
      })
    );
    setSelectedTeamIds(new Set());
    setSessionNewTeams([]);
    setPickerTarget(null);
    setInnerView("divisions");
  };

  const resetCreateTeamForm = () => {
    setCtTeamName("");
    setCtTeamCode("");
    setCtDescription("");
    setCtImageFile(null);
    setCtImageName("");
    setCtApproveNewPlayers(false);
    if (ctFileRef.current) ctFileRef.current.value = "";
  };

  const closeCreateTeamModal = () => {
    setShowCreateTeamModal(false);
    resetCreateTeamForm();
  };

  // ─── Create-team form (deferred: real create runs on Save via PUT/POST newTeams only) ───
  const handleCreateTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = addTeamsTarget.current;
    if (!target) {
      toast.error("No group selected. Use + Add Teams on a group first.");
      return;
    }
    if (!ctImageFile) {
      toast.error("Please upload a team image or logo.");
      return;
    }
    const submittedName = ctTeamName.trim();
    if (!submittedName) {
      toast.error("Team name is required.");
      return;
    }
    const submittedCode = ctTeamCode.trim();
    if (!submittedCode) {
      toast.error("Team code is required.");
      return;
    }
    const submittedDescription = ctDescription.trim();
    if (!submittedDescription) {
      toast.error("Team description is required.");
      return;
    }

    let imageBase64 = "";
    const imageFileType = normalizeImageFileTypeForApi(ctImageFile.type, ctImageFile.name);
    try {
      imageBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1] ?? "");
        reader.onerror = reject;
        reader.readAsDataURL(ctImageFile);
      });
    } catch {
      toast.error("Failed to read image file.");
      return;
    }

    const pendingId = crypto.randomUUID();
    const codeForPut = normalizeTeamCodeForDivision(submittedName, submittedCode);
    const newEntry: SessionTeam = {
      teamId: pendingId,
      teamName: submittedName,
    };
    const newChip: TeamChip = {
      teamId: pendingId,
      teamName: submittedName,
      teamCode: codeForPut,
      isOriginal: false,
      isNewlyAdded: true,
      requiresNewTeamPut: true,
      isLocalId: true,
      description: submittedDescription,
      imageBase64,
      imageFileType,
      imagePath: ctImageFile.name,
      leagueId: resolvedLeagueId ?? undefined,
    };
    const { divIdx, grpIdx } = target;
    setDivisions((prev) =>
      prev.map((div, di) => {
        if (di !== divIdx) return div;
        return {
          ...div,
          groups: div.groups.map((grp, gi) => {
            if (gi !== grpIdx) return grp;
            const dup = grp.teams.some(
              (ex) =>
                ex.teamId === newChip.teamId ||
                (ex.teamName.trim().toLowerCase() === newChip.teamName.trim().toLowerCase() &&
                  ex.requiresNewTeamPut)
            );
            if (dup) return grp;
            return { ...grp, teams: [...grp.teams, newChip] };
          }),
        };
      })
    );
    setSessionNewTeams((prev) => [...prev, newEntry]);
    resetCreateTeamForm();
    setShowCreateTeamModal(false);
    setInnerView("divisions");
    setSelectedTeamIds(new Set());
    setPickerTarget(null);
    toast.success("Team added. Save the division to create it in the tournament.");
  };

  // ─── Save division ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!activeTournamentId) { toast.error("Tournament ID not found."); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        await createDivisionTreeViaChildIds(divisions, activeTournamentId, teamsMergedForDivision);
        toast.success("Divisions created successfully.");
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getDivisions, activeTournamentId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAllTeam, activeTournamentId] });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: [QUERY_KEY.getDivisions, activeTournamentId] }),
          queryClient.refetchQueries({ queryKey: [QUERY_KEY.getAllTeam, activeTournamentId] }),
        ]);
      } else {
        const calls: Promise<unknown>[] = [];
        for (const div of divisions) {
          if (div.divisionId) {
            calls.push(updateDivision({ divisionId: div.divisionId, name: div.name, teamSnapShotIdList: [], newTeams: { teamSnapshotPutRequestList: [] } }));
            for (const grp of div.groups) {
              if (grp.divisionId) {
                const resolvedIds = grp.teams
                  .filter((t) => !t.requiresNewTeamPut)
                  .map((t) => resolveSnapshotIdForPut(t, teamsMergedForDivision))
                  .filter(Boolean);
                // Only include snapshot IDs of teams currently in the group.
                // Merging originalTeamSnapshotIds would re-add teams the user removed.
                const mergedIds = Array.from(new Set(resolvedIds));
                // PUT /division: session-created teams use `newTeams.teamSnapshotPutRequestList` only
                // (curl shape), not `teamSnapShotIdList`, even when create API returned a snapshot id.
                const putFromSessionCreate = grp.teams
                  .filter((t) => t.requiresNewTeamPut)
                  .map((chip) => ({
                    teamName: chip.teamName,
                    teamCode: chip.teamCode ?? normalizeTeamCodeForDivision(chip.teamName, undefined),
                    tournamentId: activeTournamentId,
                    divisionId: grp.divisionId!,
                    ...(chip.description ? { description: chip.description } : {}),
                    ...(chip.imageBase64 && chip.imageFileType
                      ? { image: { fileType: chip.imageFileType, base64Image: chip.imageBase64 } }
                      : {}),
                    ...(chip.captainPlayerId ? { captainPlayerId: chip.captainPlayerId } : {}),
                    ...(chip.viceCaptainPlayerId ? { viceCaptainPlayerId: chip.viceCaptainPlayerId } : {}),
                    ...(chip.wkPlayerId ? { wkPlayerId: chip.wkPlayerId } : {}),
                    ...(chip.leagueId ? { leagueId: chip.leagueId } : {}),
                    // For listing-selected teams (isLocalId=false), include the real backend teamId.
                    // For session-created teams (isLocalId=true), teamId is a client placeholder — skip it.
                    ...(chip.teamId && !chip.isLocalId ? { teamId: chip.teamId } : {}),
                    ...(chip.groundId ? { groundId: chip.groundId } : {}),
                    ...(chip.teamSnapshotId ? { teamSnapshotId: chip.teamSnapshotId } : {}),
                  }));
                const putFromNoSnapshot = grp.teams
                  .filter((t) => !t.isOriginal && !t.requiresNewTeamPut && !resolveSnapshotIdForPut(t, teamsMergedForDivision))
                  .map((chip) => ({
                    teamName: chip.teamName,
                    teamCode: chip.teamCode ?? normalizeTeamCodeForDivision(chip.teamName, undefined),
                    tournamentId: activeTournamentId,
                    divisionId: grp.divisionId!,
                    ...(chip.description ? { description: chip.description } : {}),
                    ...(chip.captainPlayerId ? { captainPlayerId: chip.captainPlayerId } : {}),
                    ...(chip.viceCaptainPlayerId ? { viceCaptainPlayerId: chip.viceCaptainPlayerId } : {}),
                    ...(chip.wkPlayerId ? { wkPlayerId: chip.wkPlayerId } : {}),
                    ...(chip.leagueId ? { leagueId: chip.leagueId } : {}),
                    ...(chip.teamId ? { teamId: chip.teamId } : {}),
                    ...(chip.groundId ? { groundId: chip.groundId } : {}),
                    ...(chip.teamSnapshotId ? { teamSnapshotId: chip.teamSnapshotId } : {}),
                  }));
                const newTeamPuts: IDivisionPutNewTeamItem[] = [...putFromSessionCreate, ...putFromNoSnapshot];
                calls.push(
                  updateDivision({
                    divisionId: grp.divisionId,
                    name: grp.name,
                    teamSnapShotIdList: mergedIds,
                    newTeams: { teamSnapshotPutRequestList: newTeamPuts },
                  })
                );
              }
            }
          }
        }
        await Promise.all(calls);
        toast.success("Divisions updated successfully.");
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getDivisions, activeTournamentId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAllTeam, activeTournamentId] });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: [QUERY_KEY.getDivisions, activeTournamentId] }),
          queryClient.refetchQueries({ queryKey: [QUERY_KEY.getAllTeam, activeTournamentId] }),
        ]);
      }
      navigate(-1);
    } catch (err) {
      console.error("[handleSave] API error:", err);
      toast.error(mode === "create" ? "Failed to create divisions." : "Failed to update divisions.");
    } finally {
      setSaving(false);
    }
  };

  const createTeamModalEl =
    showCreateTeamModal ? (
      <div className="atm-overlay" onClick={closeCreateTeamModal}>
        <div className="atm-modal atm-modal--create-team" onClick={(e) => e.stopPropagation()}>
          <div className="atm-header atm-header--create-team">
            <button type="button" className="atm-close atm-close--floating" onClick={closeCreateTeamModal} aria-label="Close">
              ×
            </button>
            <h3 className="atm-title atm-title--centered">Create New Team</h3>
          </div>
          <form className="atm-create-form-page" onSubmit={handleCreateTeamSubmit}>
            <div className="atm-create-row">
              <div className="atm-create-field">
                <label htmlFor="ct-team-image-display">
                  Team Image <span className="atm-required">*</span>
                </label>
                <div className="atm-image-input-combo">
                  <input
                    id="ct-team-image-display"
                    type="text"
                    readOnly
                    placeholder="Add Image/logo"
                    value={ctImageName}
                    className="atm-image-combo-input"
                  />
                  <input
                    ref={ctFileRef}
                    type="file"
                    accept="image/*"
                    className="atm-file-hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setCtImageFile(f);
                      setCtImageName(f?.name ?? "");
                    }}
                  />
                  <button type="button" className="atm-upload-inline" onClick={() => ctFileRef.current?.click()}>
                    <svg className="atm-upload-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M12 5v10M8 9l4-4 4 4M5 19h14"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Upload</span>
                  </button>
                </div>
              </div>
              <div className="atm-create-field">
                <label htmlFor="ct-team-name">
                  Team Name <span className="atm-required">*</span>
                </label>
                <input
                  id="ct-team-name"
                  type="text"
                  placeholder="Enter Team Name"
                  value={ctTeamName}
                  onChange={(e) => setCtTeamName(e.target.value)}
                />
              </div>
              <div className="atm-create-field">
                <label htmlFor="ct-team-code">
                  Team Code <span className="atm-required">*</span>
                </label>
                <input
                  id="ct-team-code"
                  type="text"
                  placeholder="Enter Team Code"
                  value={ctTeamCode}
                  onChange={(e) => setCtTeamCode(e.target.value)}
                />
              </div>
            </div>
            <div className="atm-create-field atm-create-field--full">
              <label htmlFor="ct-team-desc">
                Team Description <span className="atm-required">*</span>
              </label>
              <textarea
                id="ct-team-desc"
                placeholder="Description"
                value={ctDescription}
                onChange={(e) => setCtDescription(e.target.value)}
                rows={5}
                className="atm-create-textarea"
              />
            </div>
            <div className="atm-create-approve-block">
              <div className="atm-create-approve-copy">
                <span className="atm-create-approve-title">Approve New Players</span>
                <p className="atm-create-approve-desc">
                  When turned on, captains must approve a player to join the team.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={ctApproveNewPlayers}
                className={`atm-create-switch ${ctApproveNewPlayers ? "atm-create-switch--on" : ""}`}
                onClick={() => setCtApproveNewPlayers((v) => !v)}
              >
                <span className="atm-create-switch-knob" />
              </button>
            </div>
            <div className="atm-create-submit-wrap">
              <button type="submit" className="atm-create-team-submit">
                Create Team
              </button>
            </div>
          </form>
        </div>
      </div>
    ) : null;

  // ─── Render: Add Teams listing view ──────────────────────────────────────
  if (innerView === "addTeams") {
    return (
      <>
        <div className="atm-add-teams-shell">
          <div className="atm-add-teams-panel">
            <div className="atm-add-teams-watermark" aria-hidden>
              <span className="atm-add-teams-watermark-brand">CRIC CENTER</span>
              <span className="atm-add-teams-watermark-tag">LEARN, STREAM, SCORE</span>
            </div>

            <div className="atm-add-teams-header-row">
              <button type="button" className="atm-add-teams-back" onClick={() => setInnerView("divisions")}>
                <img src={Images.previous_icon} alt="Back" />
              </button>
              <h2 className="atm-add-teams-page-title">Add Teams</h2>
            </div>

            <div className="atm-add-teams-toolbar">
              <div className="atm-add-teams-tournament-dd">
                <FormDropdown
                  id="add-teams-tournament"
                  value={activeTournamentId}
                  onChange={onAddTeamsTournamentChange}
                  options={addTeamsTournamentDropdownOptions}
                  placeholder={resolvedLeagueId ? "Select tournament" : "Tournament"}
                  disabled={
                    addTeamsTournamentDropdownOptions.length === 0 &&
                    (teamSnapshotLoading || tournamentsLoading)
                  }
                  ariaLabel="Tournament"
                />
              </div>
              <button type="button" className="atm-add-teams-create-outline" onClick={() => setShowCreateTeamModal(true)}>
                <span className="atm-add-teams-create-plus">+</span>
                Create New Team
              </button>
            </div>

            <div className="atm-listing-grid atm-listing-grid--add-teams">
              {listingTeams.length === 0 && (
                <div className="atm-listing-empty-state">
                  <p className="atm-listing-empty-text">No teams available</p>
                  <button type="button" className="atm-listing-empty-btn" onClick={() => setShowCreateTeamModal(true)}>
                    + Create New Team
                  </button>
                </div>
              )}
              {listingTeams.map((t) => {
                const rowKey = addTeamsSelectionKey(t);
                const checked = selectedTeamIds.has(rowKey);
                const played =
                  (t.winMatchesCount ?? 0) + (t.lossMatchesCount ?? 0) + (t.drawMatchesCount ?? 0);
                return (
                  <div
                    key={rowKey}
                    className={`atm-listing-card atm-listing-card--add-teams ${checked ? "atm-listing-card--selected" : ""}`}
                    onClick={() => toggleTeamSelection(rowKey)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && toggleTeamSelection(rowKey)}
                  >
                    <input
                      type="checkbox"
                      className="atm-listing-checkbox"
                      checked={checked}
                      onChange={() => toggleTeamSelection(rowKey)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${t.teamName}`}
                    />
                    <div className="atm-listing-card-main">
                      <div className="atm-listing-avatar atm-listing-avatar--logo">
                        {t.teamLogoUrl ? (
                          <img src={t.teamLogoUrl} alt="" className="atm-listing-avatar-img" />
                        ) : (
                          <img src={Images.card_info_image} alt="" className="atm-listing-avatar-img" />
                        )}
                      </div>
                      <div className="atm-listing-body">
                        <span className="atm-listing-name">{t.teamName}</span>
                        <div className="atm-listing-stats">
                          <span className="atm-listing-stat atm-listing-stat--played">
                            PLAYED <b>{played}</b>
                          </span>
                          <span className="atm-listing-stat atm-listing-stat--won">
                            WON <b>{t.winMatchesCount ?? 0}</b>
                          </span>
                          <span className="atm-listing-stat atm-listing-stat--lost">
                            LOST <b>{t.lossMatchesCount ?? 0}</b>
                          </span>
                          <span className="atm-listing-stat atm-listing-stat--tie">
                            TIE <b>{t.drawMatchesCount ?? 0}</b>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="atm-add-teams-footer">
              <button
                type="button"
                className="atm-add-teams-add-btn"
                onClick={handleUpdateDivision}
                disabled={selectedTeamIds.size === 0}
              >
                <span className="atm-add-teams-add-icon">+</span>
                Add
              </button>
            </div>
          </div>
        </div>
        {createTeamModalEl}
      </>
    );
  }

  // ─── Render: Main divisions view ──────────────────────────────────────────
  return (
    <>
      <div className="create-division-page">
        <div className="create-division-header">
          <button type="button" className="create-division-back" onClick={() => navigate(-1)}>
            <img src={Images.previous_icon} alt="Back" />
          </button>
          <h2 className="create-division-title">
            {mode === "edit" ? "Edit Division" : "Create New Division"}
          </h2>
        </div>

        <div className="create-division-body">
          {divisions.map((division, divIdx) => (
            <div key={divIdx} className="division-block">
              <div className="division-block-header">
                <div className="division-block-title-wrap">
                  {editingDivision === divIdx ? (
                    <input
                      className="division-name-input"
                      value={editingDivisionName}
                      onChange={(e) => setEditingDivisionName(e.target.value)}
                      onBlur={() => saveDivisionName(divIdx)}
                      onKeyDown={(e) => e.key === "Enter" && saveDivisionName(divIdx)}
                      autoFocus
                    />
                  ) : (
                    <span className="division-name">{division.name}</span>
                  )}
                  {editingDivision !== divIdx ? (
                    <button
                      type="button"
                      className="division-edit-btn"
                      onClick={() => startEditDivisionName(divIdx)}
                      title="Edit division name"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                <button type="button" className="division-delete-btn" onClick={() => deleteDiv(divIdx)} title="Delete division">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              </div>

              <div className="division-block-groups">

                {division.groups.map((group, grpIdx) => (
                  <div key={grpIdx} className="group-block">
                    <div className="group-block-header">
                      <span className="group-label">Group {grpIdx + 1}</span>
                      <input
                        className="group-name-input"
                        placeholder="Enter Group Name"
                        value={group.name === `Group ${grpIdx + 1}` ? "" : group.name}
                        onChange={(e) => updateGroup(divIdx, grpIdx, { name: e.target.value || `Group ${grpIdx + 1}` })}
                      />
                      {canManageDivisions && (
                        <button type="button" className="add-teams-btn" onClick={() => setPickerTarget({ divIdx, grpIdx })}>
                          + Add Teams
                        </button>
                      )}
                    </div>

                    <div className="group-teams">
                      {group.teams.map((chip) => {
                        const thumbData = teamChipThumbDataUrl(chip);
                        return (
                          <span key={chip.teamId} className="team-chip">
                            <span className="team-chip-thumb" aria-hidden>
                              {thumbData ? (
                                <img src={thumbData} alt="" />
                              ) : (
                                <img src={Images.card_info_image} alt="" />
                              )}
                            </span>
                            <span className="team-chip-name">{chip.teamName}</span>
                            {!chip.isOriginal ? (
                              <button
                                type="button"
                                className="team-chip-remove"
                                onClick={() => removeTeamFromGroup(divIdx, grpIdx, chip.teamId)}
                                aria-label={`Remove ${chip.teamName}`}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                                  <path
                                    d="M18 6L6 18M6 6l12 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            ) : null}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="create-division-footer">
            {/* <button type="button" className="division-footer-edit" onClick={() => navigate(-1)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button> */}
            {canManageDivisions && (
              <button type="button" className="division-footer-save" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : mode === "edit" ? "Update Division" : "Save Division"}
              </button>
            )}
          </div>
        </div>

        {/* Modal: two options → navigate to inner view */}
        <AddTeamsModal
          open={!!pickerTarget}
          onClose={() => setPickerTarget(null)}
          onSelectExisting={() => {
            addTeamsTarget.current = pickerTarget;
            setSelectedTeamIds(new Set());
            setSnapshotRefreshKey((k) => k + 1);
            setInnerView("addTeams");
          }}
          onSelectCreate={() => {
            addTeamsTarget.current = pickerTarget;
            setSelectedTeamIds(new Set());
            setPickerTarget(null);
            setShowCreateTeamModal(true);
          }}
        />
      </div>
      {createTeamModalEl}
    </>
  );
};

export default CreateDivisionPage;
